import { z } from 'zod';
import { AnalysisStatusSchema, ConnectionStateSchema } from './enums.js';

// Authenticated, tenant-scoped server-sent events. QR payloads travel ONLY on this private stream,
// only to users with manager+ role, and are never persisted or logged.

export const QrEventSchema = z.object({
  type: z.literal('connection.qr'),
  connectionId: z.string(),
  /** Opaque QR payload to render; rotates. */
  qr: z.string(),
  expiresAt: z.string(),
});

export const ConnectionStateEventSchema = z.object({
  type: z.literal('connection.state'),
  connectionId: z.string(),
  state: ConnectionStateSchema,
  reason: z.string().nullable(),
  historyProgress: z.number().int().nullable(),
  at: z.string(),
});

export const ConversationUpdatedEventSchema = z.object({
  type: z.literal('conversation.updated'),
  conversationId: z.string(),
  revision: z.number().int(),
});

export const AnalysisUpdatedEventSchema = z.object({
  type: z.literal('analysis.updated'),
  conversationId: z.string(),
  episodeId: z.string(),
  status: AnalysisStatusSchema,
});

export const AlertUpdatedEventSchema = z.object({
  type: z.literal('alert.updated'),
  alertIds: z.array(z.string()),
});

/** Sent on (re)connect so clients resync without replaying history. */
export const SnapshotEventSchema = z.object({
  type: z.literal('snapshot'),
  connections: z.array(
    z.object({
      connectionId: z.string(),
      state: ConnectionStateSchema,
      reason: z.string().nullable(),
      historyProgress: z.number().int().nullable(),
    }),
  ),
  at: z.string(),
});

export const TenantEventSchema = z.discriminatedUnion('type', [
  QrEventSchema,
  ConnectionStateEventSchema,
  ConversationUpdatedEventSchema,
  AnalysisUpdatedEventSchema,
  AlertUpdatedEventSchema,
  SnapshotEventSchema,
]);
export type TenantEvent = z.infer<typeof TenantEventSchema>;

/** Redis pub/sub channel carrying tenant events from worker → api. */
export function tenantEventChannel(tenantId: string): string {
  return `convometry:tenant-events:${tenantId}`;
}
